import { MessagesSquare } from 'lucide-react';
import { vendorMessagingOf, type VendorMessaging } from '@peles/shared';
import { Popover } from '../common/Popover';

interface VendorMessagingControlProps {
  vendorName: string;
  /** The event–vendor link's `metadata`, where the two grants live. */
  metadata: Record<string, unknown>;
  /**
   * Called with only the key that changed, to be saved as a `metadataPatch`.
   * Never the whole metadata: a copy of what the page loaded would write back
   * every other key as it was then, undoing a switch ticked a moment earlier.
   */
  onChange: (patch: Partial<VendorMessaging>) => void;
  disabled?: boolean;
}

/**
 * The vendor's half of an event's messaging permissions (COMMUNICATIONS-DESIGN
 * D11): may its people use conversations about this event, and may they be in
 * one with the client without the office. Both off until the office says so,
 * and per event — the same caterer on another event is untouched.
 */
export function VendorMessagingControl({
  vendorName,
  metadata,
  onChange,
  disabled = false,
}: VendorMessagingControlProps) {
  const grants = vendorMessagingOf(metadata);
  const on = grants.canMessage;
  const set = (key: keyof VendorMessaging, value: boolean) => onChange({ [key]: value });

  return (
    <Popover
      align="end"
      panelClassName="w-72"
      ariaLabel={`Messaging for ${vendorName}`}
      trigger={(props) => (
        <button
          {...props}
          type="button"
          disabled={disabled}
          title={on ? 'Can use conversations on this event' : 'Messaging off for this event'}
          aria-label={`Messaging for ${vendorName}`}
          className={`min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center rounded transition-colors flex-shrink-0 disabled:opacity-40 ${
            on ? 'text-primary-600 hover:text-primary-700' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <MessagesSquare size={14} />
        </button>
      )}
    >
      {() => (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-900">Messaging on this event</p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={grants.canMessage}
              onChange={(e) => set('canMessage', e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Use conversations
              <span className="block text-xs text-gray-500">
                Start and join conversations about this event with the office.
              </span>
            </span>
          </label>
          <label className={`flex items-start gap-2 ${on ? 'cursor-pointer' : 'opacity-50'}`}>
            <input
              type="checkbox"
              checked={grants.canMessageClients}
              disabled={!on}
              onChange={(e) => set('canMessageClients', e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Message the client directly
              <span className="block text-xs text-gray-500">
                Only with clients the office has also allowed to message vendors.
              </span>
            </span>
          </label>
        </div>
      )}
    </Popover>
  );
}
